import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    selectNotifications,
    selectUnreadNotificationsCount,
} from '@interacta-shared/data-access-notification';
import { IconButtonComponent, LiveStatusComponent } from '@interacta-shared/ui';
import { createSelector, Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';
import {
    concat,
    concatMap,
    distinctUntilChanged,
    filter,
    map,
    of,
    pairwise,
    timer,
} from 'rxjs';
import { NotificationPanelService } from '../../services/notification-panel.service';

const selectUnreadCountOnceLoaded = createSelector(
    selectUnreadNotificationsCount,
    selectNotifications,
    (count, notifications) => ({
        count,
        loaded: notifications.nextPageToken.tag !== 'firstLoading',
    }),
);

@Component({
    selector: 'interacta-notification-panel-toggle-button',
    imports: [IconButtonComponent, LiveStatusComponent, NgClass, TranslatePipe],
    templateUrl: './notification-panel-toggle-button.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationPanelToggleButtonComponent {
    private readonly store = inject(Store);
    private readonly notificationPanelService = inject(
        NotificationPanelService,
    );

    readonly unreadNotificationsCount = this.store.selectSignal(
        selectUnreadNotificationsCount,
    );
    readonly notifications = this.store.selectSignal(selectNotifications);
    readonly isPanelOpen = this.notificationPanelService.isOpen;
    readonly newNotificationToggle = toSignal(
        this.store.select(selectNotifications).pipe(
            filter((notifications) => notifications.list.length > 0),
            concatMap(() =>
                concat(of(true), timer(3000).pipe(map(() => false))),
            ),
        ),
    );

    readonly newUnreadCount = toSignal(
        this.store.select(selectUnreadCountOnceLoaded).pipe(
            distinctUntilChanged(
                (a, b) => a.count === b.count && a.loaded === b.loaded,
            ),
            pairwise(),
            map(([previous, current]) =>
                previous.loaded && current.count > previous.count
                    ? current.count
                    : null,
            ),
        ),
        { initialValue: null },
    );

    openNotificationPanel(): void {
        this.notificationPanelService.open();
    }
}
